// client/src/components/marketer/project/modals/ProjectPreviewModal.js
import React, { useEffect, useState } from 'react';
import '../../../../styles/marketer/modals/ProjectPreviewModal.css';
import { linkifyHtml } from '../../../../utils/linkify';

function toDateStr(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '-';
  return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')}`;
}

export default function ProjectPreviewModal({
  open, onClose,
  serviceType,
  product,
  cover, gallery,
  recruit, reward, perReward,
  schedule,
  description,
  manager,
}) {
  const [galleryUrls, setGalleryUrls] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (!open) return;
    const gs = Array.isArray(gallery) ? gallery : [];
    const created = gs.map(f => (f instanceof File ? URL.createObjectURL(f) : (typeof f === 'string' ? f : ''))).filter(Boolean);
    setGalleryUrls(created);
    return () => {
      created.forEach(u => { try { URL.revokeObjectURL(u); } catch {} });
    };
  }, [open, gallery]);

  useEffect(() => {
    if (open) {
      setCurrentImageIndex(0);
    }
  }, [open]);

  // 모달이 열릴 때 body 스크롤 방지
  useEffect(() => {
    if (open) {
      // 현재 스크롤 위치 저장
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflowY = 'scroll'; // 스크롤바 자리 유지
      
      return () => {
        // 모달이 닫힐 때 원래대로 복원
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflowY = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  if (!open) return null;

  // 제품명 추출
  const getProductName = () => {
    if (serviceType === 'delivery' || serviceType === 'purchase') {
      return product?.name || '광고 대상';
    } else if (serviceType === 'visit') {
      return product?.placeName || '광고 대상';
    } else if (serviceType === 'free') {
      return product?.customTitle || '광고 대상';
    }
    return '광고 대상';
  };

  // 광고 형식 추출
  const getServiceTypeLabel = () => {
    if (serviceType === 'delivery') return '제품 배송형';
    if (serviceType === 'purchase') return '제품 구매형';
    if (serviceType === 'visit') return '장소 방문형';
    if (serviceType === 'free') return '자율형';
    return '';
  };

  // 서비스 타입별 안내 문구
  const getServiceTypeDescription = () => {
    if (serviceType === 'delivery') {
      return '해당 프로젝트는 광고주가 리뷰할 제품을 제공해주는 프로젝트 입니다. 프로필에 설정된 주소로 제품이 배송됩니다.';
    }
    if (serviceType === 'purchase') {
      return '해당 프로젝트는 인플루언서님이 리뷰할 제품을 직접 구매하셔야하는 프로젝트입니다, 제품은 제공되지 않습니다.';
    }
    if (serviceType === 'visit') {
      return '해당 프로젝트는 장소/서비스를 홍보하는 프로젝트 입니다.';
    }
    if (serviceType === 'free') {
      return '해당 프로젝트는 자율형식으로 진행됩니다. 광고주가 제시하는 상세 가이드를 잘 확인하세요';
    }
    return '';
  };

  // 전체 이미지 배열 (대표사진 + 세부사진)
  const getCoverUrl = () => {
    if (cover instanceof File) {
      return URL.createObjectURL(cover);
    } else if (typeof cover === 'string' && cover) {
      return cover;
    }
    return '';
  };

  const coverUrl = getCoverUrl();
  const allImages = [coverUrl, ...galleryUrls].filter(Boolean);

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="project-preview-modal mbm-overlay">
      <div className="project-preview-modal__content mbm" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="project-preview-modal__header-bar mbm-head">
          <div className="project-preview-modal__header-title mbm-title">
            <span>프로젝트 미리보기</span>
          </div>
          <button onClick={onClose} aria-label="close" className="project-preview-modal__close mbm-close">×</button>
        </div>

        <div className="project-preview-modal__body">
          
          {/* 프로젝트 소개 문구 */}
          <div className="project-preview-modal__intro">
            <strong>{getProductName()}</strong>을 홍보하는 <strong>{getServiceTypeLabel()}</strong> 광고 프로젝트 입니다.
          </div>

          {/* 서비스 타입별 안내 */}
          <div className="project-preview-modal__service-notice">
            {getServiceTypeDescription()}
          </div>
          
          {/* 섹션 2: 진행 프로세스 안내 */}
          <div>
            <div className="project-preview-modal__guide-label">Project Journey</div>
            <h3 className="project-preview-modal__section-title">이 프로젝트는 이렇게 진행됩니다</h3>
            <div className="project-preview-modal__process-container">
              <div className="project-preview-modal__process-step">
                <div className="project-preview-modal__process-icon">✉️</div>
                <div className="project-preview-modal__process-title">1. 프로젝트 지원</div>
                <div className="project-preview-modal__process-desc">인플루언서가 선정 마감 시까지 프로젝트에 지원합니다.</div>
              </div>
              <div className="project-preview-modal__process-step">
                <div className="project-preview-modal__process-icon">✅</div>
                <div className="project-preview-modal__process-title">2. 인플루언서 선정</div>
                <div className="project-preview-modal__process-desc">광고주가 콘텐츠 홍보를 맡길 인플루언서를 선정합니다.</div>
              </div>
              <div className="project-preview-modal__process-step">
                <div className="project-preview-modal__process-icon">✍️</div>
                <div className="project-preview-modal__process-title">3. 계약 진행</div>
                <div className="project-preview-modal__process-desc">선정된 인플루언서가 프로젝트 참여 계약에 서명합니다.</div>
              </div>
              <div className="project-preview-modal__process-step">
                <div className="project-preview-modal__process-icon">📸</div>
                <div className="project-preview-modal__process-title">4. 콘텐츠 업로드</div>
                <div className="project-preview-modal__process-desc">인플루언서가 제품/서비스 리뷰 콘텐츠를 가이드에 맞춰 업로드합니다.</div>
              </div>
              <div className="project-preview-modal__process-step">
                <div className="project-preview-modal__process-icon">💰</div>
                <div className="project-preview-modal__process-title">5. 리워드 지급</div>
                <div className="project-preview-modal__process-desc">광고주가 업로드된 콘텐츠를 확인 후, 승인하면 리워드가 인플루언서에게 지급됩니다.</div>
              </div>
            </div>
          </div>

          {/* 공통 가이드 박스 */}
          <div className="project-preview-modal__common-guide">
            <div className="project-preview-modal__common-guide-header">
              <span className="project-preview-modal__common-guide-icon">📋</span>
              <h3 className="project-preview-modal__common-guide-title">공통 가이드</h3>
            </div>
            <ul className="project-preview-modal__common-guide-list">
              <li>선정 후, 계약 시 결과물을 기한 내 업로드 해주셔야 합니다. 정상적으로 진행되지 않을 시, 셀프애드 이용이 제한됩니다.</li>
              <li>결과물(광고 콘텐츠)의 최소 게시 유지 기간은 3개월입니다. 3개월 이상 콘텐츠를 유지해주셔야 합니다.</li>
              <li>보상을 받는 콘텐츠의 경우 보상을 받고 리뷰를 진행하는 것임을 명시해주세요.</li>
              <li>프로젝트에 선정되고, 문의 사항이 생길 시, 셀프애드를 통해 문의해주시면 됩니다.</li>
              <li>콘텐츠는 반드시 프로젝트 지원시 선택한 SNS 계정에 업로드 되어야합니다.</li>
              <li>제품을 제공받고 업로드를 하지 않는 경우, 콘텐츠가 제대로 게시되지 않는 경우 등 고의 또는 부주의로 인해 비정상적으로 프로젝트가 진행될 시 피해 금액이 위약금으로 부과됩니다.</li>
            </ul>
          </div>

          {/* 섹션 3: 상세 정보 그리드 */}
          <div className="project-preview-modal__main-grid">
            
            {/* 왼쪽 컬럼: 프로젝트 및 미션 설명 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              
              {/* 프로젝트 기본 정보 */}
              <div className="project-preview-modal__card">
                <div className="project-preview-modal__card-title-box">
                  <div className="project-preview-modal__card-title">📦 프로젝트 대상 정보</div>
                </div>

                {/* 이미지 슬라이더 */}
                {allImages.length > 0 && (
                  <div className="project-preview-modal__image-slider">
                    <div className="project-preview-modal__image-container">
                      <img 
                        src={allImages[currentImageIndex]} 
                        alt={`프로젝트 이미지 ${currentImageIndex + 1}`}
                        className="project-preview-modal__slider-image"
                      />
                      {allImages.length > 1 && (
                        <>
                          <button 
                            className="project-preview-modal__slider-btn project-preview-modal__slider-btn--prev"
                            onClick={handlePrevImage}
                            aria-label="이전 이미지"
                          >
                            ‹
                          </button>
                          <button 
                            className="project-preview-modal__slider-btn project-preview-modal__slider-btn--next"
                            onClick={handleNextImage}
                            aria-label="다음 이미지"
                          >
                            ›
                          </button>
                        </>
                      )}
                    </div>
                    {allImages.length > 1 && (
                      <div className="project-preview-modal__slider-indicator">
                        {currentImageIndex + 1} / {allImages.length}
                      </div>
                    )}
                  </div>
                )}

                <div className="project-preview-modal__info-list">
                  {serviceType === 'delivery' && (
                    <>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🎁</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">광고할 제품</div>
                          <div className="project-preview-modal__info-value">{product?.name || '-'}</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🏷️</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">제품 판매처의 제품 가격</div>
                          <div className="project-preview-modal__info-value">{Number(product?.price||0).toLocaleString()}원</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🔗</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">제품 관련 참고 URL</div>
                          <div className="project-preview-modal__info-value" style={{ color: '#2f6bff' }}>{product?.url || '-'}</div>
                        </div>
                      </div>
                    </>
                  )}
                  {serviceType === 'purchase' && (
                    <>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🛒</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">리뷰할 제품</div>
                          <div className="project-preview-modal__info-value">{product?.name || '-'}</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🏷️</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">제품 가격</div>
                          <div className="project-preview-modal__info-value">{Number(product?.price||0).toLocaleString()}원</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🔗</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">제품 관련 (구매)참고 URL</div>
                          <div className="project-preview-modal__info-value" style={{ color: '#2f6bff' }}>{product?.purchasePlace || '-'}</div>
                        </div>
                      </div>
                    </>
                  )}
                  {serviceType === 'visit' && (
                    <>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🏛️</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">장소/서비스 명</div>
                          <div className="project-preview-modal__info-value">{product?.placeName || '-'}</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🗺️</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">주소 or URL</div>
                          <div className="project-preview-modal__info-value">{product?.placeAddress || '-'}</div>
                        </div>
                      </div>
                    </>
                  )}
                  {serviceType === 'free' && (
                    <>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">📝</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">제품명</div>
                          <div className="project-preview-modal__info-value">{product?.customTitle || '-'}</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">ℹ️</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">참고 설명</div>
                          <div className="project-preview-modal__info-value">{product?.customDesc || '-'}</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* 미션 설명 및 가이드 */}
              <div className="project-preview-modal__card">
                <div className="project-preview-modal__card-title project-preview-modal__card-title--large">⚠️ 광고주 요청 사항 (중요)</div>
                <div className="project-preview-modal__info-box-blue">
                  <div className="project-preview-modal__info-box-title">💡 선정된 인플루언서가 수행할 활동입니다</div>
                  <div style={{ fontSize: '13px', color: '#1e40af', lineHeight: 1.5 }}>
                    아래 광고주 요청사항에 맞춰 콘텐츠를 제작하고 업로드해주세요. <br/>
                    가이드가 준수되지 않을 시, 콘텐츠 제작 및 업로드가 재요청 될 수 있습니다.
                  </div>
                </div>
                <div style={{ 
                  fontSize: '15px', 
                  lineHeight: 1.8, 
                  color: '#334155',
                  padding: '12px',
                  background: '#f8fafc',
                  borderRadius: '16px'
                }}>
                  <div dangerouslySetInnerHTML={{ __html: linkifyHtml(description) || '<span style="color:#9ca3af">상세 가이드 내용이 등록되지 않았습니다.</span>' }} />
                </div>
              </div>
            </div>

            {/* 오른쪽 컬럼: 리워드, 일정, 담당자 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              
              {/* 리워드 혜택 */}
              <div className="project-preview-modal__card">
                <div className="project-preview-modal__card-title">💰 참여 리워드</div>
                <div className="project-preview-modal__reward-large">
                  <div style={{ fontSize: '14px', fontWeight: 700, opacity: 0.9 }}>기본 리워드</div>
                  <div className="project-preview-modal__reward-amount">{Number(reward?.rewardPointPerCreator||0).toLocaleString()} pt</div>
                  <div style={{ fontSize: '13px', opacity: 0.8 }}>콘텐츠 최종 승인 시 즉시 지급</div>
                </div>
                {perReward?.use && (
                  <>
                    <div className="project-preview-modal__info-box-blue" style={{ background: '#f0fdf4', borderColor: '#dcfce7' }}>
                      <div className="project-preview-modal__info-box-title" style={{ color: '#166534' }}>📈 성과형 리워드 (CPS)</div>
                      <div style={{ fontSize: '15px', fontWeight: 800, color: '#15803d' }}>
                        구매 고객 1명당 {Number(perReward?.rewardPerBuyer||0).toLocaleString()} pt
                        {perReward?.budgetTotal && perReward?.settlementCount && reward?.headcount && (
                          <span> / 회차당 최대 {Math.floor(Number(perReward.budgetTotal) / Number(reward.headcount) / Number(perReward.settlementCount)).toLocaleString()} pt</span>
                        )}
                        {perReward?.settlementCount && (
                          <span> / 총 {perReward.settlementCount}회 결산</span>
                        )}
                      </div>
                    </div>
                    <div className="project-preview-modal__cps-info">
                      <div className="project-preview-modal__cps-info-title">💡 성과형 리워드란?</div>
                      <div className="project-preview-modal__cps-info-text">
                        계약 시, 광고주가 인플루언서에게 URL을 제공합니다. 해당 URL을 통해 고객이 제품을 구매하게 될 시, 인플루언서에게 광고비가 추가로 지급됩니다.
                      </div>
                    </div>
                  </>
                )}
                <div style={{ textAlign: 'center', fontSize: '13px', color: '#64748b' }}>
                  현재 <b>{Number(reward?.headcount||0)}명</b>의 인플루언서를 찾고 있습니다.
                </div>
              </div>

              {/* 프로젝트 일정 */}
              <div className="project-preview-modal__card">
                <div className="project-preview-modal__card-title">📅 주요 일정 안내</div>
                <div className="project-preview-modal__info-list">
                  <div className="project-preview-modal__info-item">
                    <div className="project-preview-modal__info-icon-sm">⏳</div>
                    <div className="project-preview-modal__info-text-box">
                      <div className="project-preview-modal__info-label">모집 및 선정 마감</div>
                      <div className="project-preview-modal__info-value">{toDateStr(schedule?.recruitEnd)} 까지</div>
                      <div className="project-preview-modal__info-subtext">선정 시 안내 문자가 발송됩니다.</div>
                    </div>
                  </div>
                  <div className="project-preview-modal__info-item">
                    <div className="project-preview-modal__info-icon-sm">📤</div>
                    <div className="project-preview-modal__info-text-box">
                      <div className="project-preview-modal__info-label">콘텐츠 제출 마감</div>
                      <div className="project-preview-modal__info-value">{toDateStr(schedule?.submitEnd)} 까지</div>
                      <div className="project-preview-modal__info-subtext">반드시 기한 내에 업로드 해주세요.</div>
                    </div>
                  </div>
                  {perReward?.use && schedule?.settlementDates?.length > 0 && (
                    <div className="project-preview-modal__info-item">
                      <div className="project-preview-modal__info-icon-sm">💰</div>
                      <div className="project-preview-modal__info-text-box">
                        <div className="project-preview-modal__info-label">CPS 결산일</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {schedule.settlementDates.map((date, idx) => (
                            <div key={idx} className="project-preview-modal__info-value" style={{ fontSize: '14px' }}>
                              {idx + 1}회차: {toDateStr(date)}
                            </div>
                          ))}
                        </div>
                        <div className="project-preview-modal__info-subtext">결산일에 성과형 리워드가 정산됩니다.</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 담당자 연락처 */}
              <div className="project-preview-modal__card project-preview-modal__card--blurred" style={{ background: '#f8fafc', position: 'relative' }}>
                <div className="project-preview-modal__card-title">📞 프로젝트 지원 센터</div>
                <div className="project-preview-modal__info-list" style={{ filter: 'blur(5px)' }}>
                  <div className="project-preview-modal__info-item" style={{ background: 'white' }}>
                    <div className="project-preview-modal__info-text-box">
                      <div className="project-preview-modal__info-label">문의 연락처</div>
                      <div className="project-preview-modal__info-value">{manager?.phone || '-'}</div>
                    </div>
                  </div>
                  <div className="project-preview-modal__info-item" style={{ background: 'white' }}>
                    <div className="project-preview-modal__info-text-box">
                      <div className="project-preview-modal__info-label">문의 이메일</div>
                      <div className="project-preview-modal__info-value">{manager?.email || '-'}</div>
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center', filter: 'blur(5px)' }}>
                  응답 가능 시간: {manager?.officeHour || '상시'}
                </div>
                <div className="project-preview-modal__blur-overlay">
                  <div className="project-preview-modal__blur-text">프로젝트 계약 시 볼 수 있습니다.</div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
